// src/components/SystemLogs.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchSystemLogs, fetchSystemLogTimeline } from '../api';
import LoadingSpinner from './LoadingSpinner';

const formatDateTime = (value) => {
  if (!value) {
    return '—';
  }
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleString('pl-PL');
  } catch (error) {
    return value;
  }
};

const stringifyPayload = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }
  try {
    if (typeof value === 'string') {
      return JSON.stringify(JSON.parse(value), null, 2);
    }
    return JSON.stringify(value, null, 2);
  } catch (error) {
    if (typeof value === 'string') {
      return value;
    }
    return String(value);
  }
};

const defaultFilters = {
  search: '',
  source: '',
  eventType: '',
  status: '',
  dateFrom: '',
  dateTo: ''
};

const SystemLogs = ({ token }) => {
  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
  const [metrics, setMetrics] = useState({ total: 0, last24h: 0 });
  const [facets, setFacets] = useState({ sources: [], eventTypes: [], statuses: [] });
  const [filters, setFilters] = useState(defaultFilters);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState('');
  const selectedLogRef = useRef(null);
  useEffect(() => {
    selectedLogRef.current = selectedLog;
  }, [selectedLog]);

  const appliedParams = useMemo(() => ({
    page: meta.page,
    limit: meta.limit,
    search: filters.search || undefined,
    sources: filters.source ? filters.source : undefined,
    eventTypes: filters.eventType ? filters.eventType : undefined,
    statuses: filters.status ? filters.status : undefined,
    dateFrom: filters.dateFrom || undefined,
    dateTo: filters.dateTo || undefined
  }), [filters, meta.limit, meta.page]);

  const loadLogs = useCallback(async () => {
    if (!token) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetchSystemLogs(token, appliedParams);
      const { data, meta: metaData, facets: facetData, metrics: metricsData } = response.data;
      setLogs(data || []);
      setMeta({
        page: metaData?.page || 1,
        limit: metaData?.limit || 25,
        total: metaData?.total || 0,
        totalPages: metaData?.totalPages || 1
      });
      setFacets({
        sources: facetData?.sources || [],
        eventTypes: facetData?.eventTypes || [],
        statuses: facetData?.statuses || []
      });
      setMetrics({
        total: metricsData?.total || 0,
        last24h: metricsData?.last24h || 0
      });
      const currentSelection = selectedLogRef.current;
      if (data && currentSelection) {
        const stillVisible = data.find((item) => item.id === currentSelection.id);
        if (!stillVisible) {
          setSelectedLog(null);
          setTimeline([]);
        }
      }
    } catch (err) {
      console.error('SystemLogs load error:', err);
      setError('Nie udało się pobrać logów. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  }, [token, appliedParams]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    if (!token || !selectedLog || !selectedLog.correlation_id) {
      setTimeline([]);
      setTimelineError('');
      return;
    }

    let isCancelled = false;
    const loadTimeline = async () => {
      setTimelineLoading(true);
      setTimelineError('');
      try {
        const response = await fetchSystemLogTimeline(token, selectedLog.correlation_id);
        if (!isCancelled) {
          setTimeline(response.data || []);
        }
      } catch (err) {
        console.error('SystemLogs timeline error:', err);
        if (!isCancelled) {
          setTimelineError('Nie udało się załadować osi czasu.');
        }
      } finally {
        if (!isCancelled) {
          setTimelineLoading(false);
        }
      }
    };

    loadTimeline();
    return () => {
      isCancelled = true;
    };
  }, [selectedLog, token]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setMeta((prev) => ({ ...prev, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters(defaultFilters);
    setMeta((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (delta) => {
    setMeta((prev) => {
      const nextPage = Math.min(Math.max(prev.page + delta, 1), prev.totalPages || 1);
      if (nextPage === prev.page) {
        return prev;
      }
      return { ...prev, page: nextPage };
    });
  };

  const handlePageSizeChange = (event) => {
    const nextLimit = Number.parseInt(event.target.value, 10) || 25;
    setMeta((prev) => {
      const total = prev.total || 0;
      const nextTotalPages = Math.max(1, Math.ceil(total / nextLimit));
      return { ...prev, page: 1, limit: nextLimit, total, totalPages: nextTotalPages };
    });
  };

  const summaryCards = [
    {
      label: 'Wszystkie logi',
      value: metrics.total,
      description: 'Łączna liczba wpisów w historii'
    },
    {
      label: 'Ostatnie 24h',
      value: metrics.last24h,
      description: 'Nowe zdarzenia z ostatniej doby'
    },
    {
      label: 'Bieżące wyniki',
      value: meta.total,
      description: 'Liczba wpisów zgodnych z filtrami'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {summaryCards.map((card) => (
          <div key={card.label} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="text-sm text-gray-500 font-medium">{card.label}</div>
            <div className="text-2xl font-semibold text-gray-900 mt-2">{card.value}</div>
            <div className="text-xs text-gray-400 mt-1">{card.description}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Szukaj</label>
            <input
              type="text"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="fraza, e-mail, ID zamówienia..."
              value={filters.search}
              onChange={(event) => handleFilterChange('search', event.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Źródło</label>
            <select
              className="w-full border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={filters.source}
              onChange={(event) => handleFilterChange('source', event.target.value)}
            >
              <option value="">Wszystkie</option>
              {facets.sources.map((source) => (
                <option key={source} value={source}>{source}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Typ zdarzenia</label>
            <select
              className="w-full border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={filters.eventType}
              onChange={(event) => handleFilterChange('eventType', event.target.value)}
            >
              <option value="">Wszystkie</option>
              {facets.eventTypes.map((eventType) => (
                <option key={eventType} value={eventType}>{eventType}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Status</label>
            <select
              className="w-full border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={filters.status}
              onChange={(event) => handleFilterChange('status', event.target.value)}
            >
              <option value="">Wszystkie</option>
              {facets.statuses.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Data od</label>
            <input
              type="date"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={filters.dateFrom}
              onChange={(event) => handleFilterChange('dateFrom', event.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Data do</label>
            <input
              type="date"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={filters.dateTo}
              onChange={(event) => handleFilterChange('dateTo', event.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between mt-4 gap-3">
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-500">Na stronę:</label>
            <select
              className="border border-gray-200 rounded-xl px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={meta.limit}
              onChange={handlePageSizeChange}
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={handleResetFilters}
            className="text-sm text-indigo-600 font-medium hover:text-indigo-700"
          >
            Wyczyść filtry
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
        <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Historia zdarzeń</h2>
          <span className="text-sm text-gray-500">{meta.total} wpisów</span>
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="py-12">
              <LoadingSpinner text="Ładowanie logów..." />
            </div>
          ) : error ? (
            <div className="py-12 text-center text-red-600 font-medium">{error}</div>
          ) : logs.length === 0 ? (
            <div className="py-12 text-center text-gray-500">Brak wpisów spełniających warunki filtrowania.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Czas</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Źródło</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Typ zdarzenia</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Opis</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {logs.map((log) => (
                  <tr
                    key={log.id}
                    onClick={() => setSelectedLog(log)}
                    className={`cursor-pointer transition-colors ${selectedLog?.id === log.id ? 'bg-indigo-50/80' : 'hover:bg-gray-50'}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDateTime(log.created_at)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                        {log.source}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{log.event_type}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${log.status === 'success' ? 'bg-green-100 text-green-700' : log.status === 'error' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                        {log.status || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={log.message || ''}>{log.message || '—'}</td>
                    <td className="px-6 py-4 text-right text-sm text-indigo-600 font-medium">
                      {selectedLog?.id === log.id ? 'Zaznaczone' : 'Szczegóły'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="border-t border-gray-100 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-gray-500">
            Strona {meta.page} z {meta.totalPages} • {meta.total} wyników
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => handlePageChange(-1)}
              disabled={meta.page <= 1}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Poprzednia
            </button>
            <button
              type="button"
              onClick={() => handlePageChange(1)}
              disabled={meta.page >= meta.totalPages}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Następna
            </button>
          </div>
        </div>
      </div>

      {selectedLog && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Szczegóły wpisu</h3>
                <p className="text-sm text-gray-500">ID: {selectedLog.id}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedLog(null)}
                className="text-sm text-gray-400 hover:text-gray-600"
              >
                Zamknij
              </button>
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Czas</dt>
                <dd className="text-sm text-gray-900 mt-1">{formatDateTime(selectedLog.created_at)}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Źródło</dt>
                <dd className="text-sm text-gray-900 mt-1">{selectedLog.source}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Typ zdarzenia</dt>
                <dd className="text-sm text-gray-900 mt-1 break-words">{selectedLog.event_type}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</dt>
                <dd className="text-sm text-gray-900 mt-1">{selectedLog.status || '—'}</dd>
              </div>
              {selectedLog.correlation_id && (
                <div>
                  <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Correlation ID</dt>
                  <dd className="text-sm text-gray-900 mt-1 break-all">{selectedLog.correlation_id}</dd>
                </div>
              )}
              {selectedLog.woo_order_id && (
                <div>
                  <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Woo Order ID</dt>
                  <dd className="text-sm text-gray-900 mt-1">{selectedLog.woo_order_id}</dd>
                </div>
              )}
              {selectedLog.customer_email && (
                <div>
                  <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide">E-mail</dt>
                  <dd className="text-sm text-gray-900 mt-1 break-all">{selectedLog.customer_email}</dd>
                </div>
              )}
              {selectedLog.voucher_code && (
                <div>
                  <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Kod vouchera</dt>
                  <dd className="text-sm text-gray-900 mt-1 break-all">{selectedLog.voucher_code}</dd>
                </div>
              )}
            </dl>

            <div className="mt-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Wiadomość</h4>
              <p className="text-sm text-gray-700 whitespace-pre-line bg-gray-50 border border-gray-100 rounded-xl p-4">
                {selectedLog.message || '—'}
              </p>
            </div>

            <div className="mt-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Metadata</h4>
              <pre className="text-xs text-gray-700 whitespace-pre-wrap bg-gray-50 border border-gray-100 rounded-xl p-4 overflow-auto max-h-48">
                {stringifyPayload(selectedLog.metadata)}
              </pre>
            </div>

            <div className="mt-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Payload</h4>
              <pre className="text-xs text-gray-700 whitespace-pre-wrap bg-gray-50 border border-gray-100 rounded-xl p-4 overflow-auto max-h-48">
                {stringifyPayload(selectedLog.payload)}
              </pre>
            </div>

            {selectedLog.error_details && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-red-700 mb-2">Szczegóły błędu</h4>
                <pre className="text-xs text-red-800 whitespace-pre-wrap bg-red-50 border border-red-100 rounded-xl p-4 overflow-auto max-h-48">
                  {stringifyPayload(selectedLog.error_details)}
                </pre>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Oś czasu</h3>
              {selectedLog.correlation_id ? (
                <span className="text-xs text-gray-500">{selectedLog.correlation_id}</span>
              ) : null}
            </div>

            {!selectedLog.correlation_id ? (
              <div className="text-sm text-gray-500">
                Brak powiązanego correlation ID dla tego wpisu.
              </div>
            ) : timelineLoading ? (
              <div className="py-8">
                <LoadingSpinner text="Ładowanie osi czasu..." size="sm" />
              </div>
            ) : timelineError ? (
              <div className="text-sm text-red-600">{timelineError}</div>
            ) : timeline.length === 0 ? (
              <div className="text-sm text-gray-500">Brak dodatkowych wpisów dla tego identyfikatora.</div>
            ) : (
              <ul className="space-y-4 relative">
                <div className="absolute left-2 top-0 bottom-0 border-l-2 border-indigo-100" aria-hidden="true"></div>
                {timeline.map((item) => (
                  <li key={item.id} className="pl-6 relative">
                    <span className={`absolute left-0 top-2 w-3 h-3 rounded-full border-2 ${item.id === selectedLog.id ? 'bg-indigo-600 border-indigo-600' : 'bg-white border-indigo-200'}`}></span>
                    <div className="bg-indigo-50/60 rounded-xl p-4 border border-indigo-100">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="text-sm font-semibold text-indigo-900">{item.event_type}</div>
                        <div className="text-xs text-indigo-700">{formatDateTime(item.created_at)}</div>
                      </div>
                      <div className="text-xs text-indigo-600 mt-1">{item.source}</div>
                      {item.message && (
                        <p className="text-sm text-gray-700 mt-2 whitespace-pre-line">{item.message}</p>
                      )}
                      {item.status && (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold mt-3 ${item.status === 'success' ? 'bg-green-100 text-green-700' : item.status === 'error' ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-700'}`}>
                          {item.status}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemLogs;
